// Services
import api from "./apiClient"

// Stores
import { useUserStore } from "@/stores/user"

// Types
import { CheckoutSessionItem } from "@/shared/types/CheckoutSession"
import StripePrice from "@/shared/enums/StripePrice"

export enum UserFlow {
  REPORT = "report",
  COACHING = "coaching",
  RESUME = "resume"
}

export const GTM_EVENT_NAMES = [
  "addToCart",
  "adClick",
  "beginCheckout",
  "booking",
  "groupCoachingInterestNo",
  "groupCoachingInterestYes",
  "invoiceCharged",
  "pageView",
  "purchase",
  "quizComplete",
  "quizStart",
  "newQuizComplete",
  "newQuizStart",
  "newAssessmentView",
  "reschedule",
  "signUp",
  "viewItemList",
  "viewSelectCoach"
] as const

export type GtmEventName = (typeof GTM_EVENT_NAMES)[number]

export type DataLayer = {
  user?: {
    id?: number
    email?: string
    phoneNumber?: string
    zipCode?: string
  }
  userFlow?: UserFlow
  value?: number
  currency?: string
  sessionId?: string | null
  stripePrices?: StripePrice[]
  paymentIntent?: string | null
  items?: CheckoutSessionItem[]
  eventTypeSlug?: string
  adClickDetails?: {
    adPlatform?: string
    adFormat?: string
    linkUrl?: string
    jobId?: number
    positionInList?: number
  }
  transactionId?: string | null
}

export default class GtmService {
  // When set to true, events will be sent to the data layer, even in dev and staging environments.
  // Keep this set to false, unless you are testing GTM events.
  private static fireEventsInDev = false

  // STATIC METHODS
  static async addToCartEvent(userFlow: UserFlow) {
    await this.pushEventToDataLayer("addToCart", { userFlow })
  }

  static async adzunaClickEvent(adClickDetails: DataLayer["adClickDetails"]) {
    await this.pushEventToDataLayer("adClick", { adClickDetails })
  }

  /**
   * Sends a begin checkout event to the data layer.
   */
  static async beginCheckoutEvent(userFlow: UserFlow, items: CheckoutSessionItem[]) {
    try {
      await this.pushEventToDataLayer("beginCheckout", {
        userFlow,
        items
      })
    } catch (error) {
      console.error("Error in beginCheckoutEvent", error)
    }
  }

  /**
   * Sends an event when a coaching session is booked or rescheduled.
   */
  static async bookingEvent(eventTypeSlug: string) {
    await this.pushEventToDataLayer("booking", { eventTypeSlug })
  }

  /**
   * Sends an event when a coaching session is rescheduled.
   */
  static async rescheduleEvent(eventTypeSlug: string) {
    await this.pushEventToDataLayer("reschedule", { eventTypeSlug })
  }

  /**
   * Sends an event when a user indicates they are interested or not interested in group coaching.
   */
  static async groupCoachingInterestEvent(interested: boolean) {
    await this.pushEventToDataLayer(`groupCoachingInterest${interested ? "Yes" : "No"}`)
  }

  /**
   * Sends a page view event to the data layer.
   * - Fired from the router after each page view.
   * - Used to manually trigger page view events in GTM, that are accurate in the SPA.
   * - Initially implemented specifically for Meta ads, since the default behaviour tracks url fragment changes, which is not appropriate in our case.
   */
  static async pageViewEvent() {
    await this.pushEventToDataLayer("pageView")
  }

  /**
   * Sends an Invoice Charged event to the data layer.
   */
  static async invoiceChargedEvent(userFlow: UserFlow, invoiceId: string, value: number, stripePrices: StripePrice[]) {
    try {
      if (!value) {
        throw new Error("Value not provided for invoice charge event")
      }

      const purchaseData: DataLayer = {
        userFlow,
        value,
        currency: "USD",
        stripePrices: stripePrices || [],
        transactionId: invoiceId
      }

      await this.pushEventToDataLayer("invoiceCharged", purchaseData)
    } catch (error) {
      console.error("Error in invoiceChargedEvent", error)
    }
  }
  /**
   * Sends a purchase event to the data layer.
   */
  static async purchaseEvent(userFlow: UserFlow, sessionId: string, fallbackValue: number) {
    try {
      const response = await api.stripe.getCheckoutSession(sessionId)

      const session = response?.session

      if (!session) {
        throw new Error("Checkout session not found")
      }

      if (session.status !== "complete") {
        throw new Error("Checkout session not completed")
      }

      const purchaseData: DataLayer = {
        userFlow,
        value: session.value || fallbackValue,
        currency: session.currency || "USD",
        sessionId: session.id || sessionId,
        paymentIntent: session.paymentIntent || "",
        items: session?.items || [],
        transactionId: session.id || sessionId || session.paymentIntent
      }

      await this.pushEventToDataLayer("purchase", purchaseData)
    } catch (error) {
      console.error("Error in purchaseEvent", error)
    }
  }

  /**
   * Send a quiz complete event to the data layer.
   * - Fired after QUBL quiz is completed.
   */
  static async quizCompleteEvent(userFlow: UserFlow) {
    await this.pushEventToDataLayer("quizComplete", { userFlow })
  }

  /**
   * Send a quiz complete event to the data layer for strategic coaching call feasibility flow.
   * - Fired after email is provided at the end of the questionnaire flow.
   */
  static async newQuizCompleteEvent(userFlow: UserFlow) {
    await this.pushEventToDataLayer("newQuizComplete", { userFlow })
  }

  /**
   * Send a quiz start event to the data layer.
   * - Fired when the user starts the QUBL quiz.
   */
  static async quizStartEvent(userFlow: UserFlow) {
    await this.pushEventToDataLayer("quizStart", { userFlow })
  }

  /**
   * Send a quiz start event to the data layer for strategic coaching call feasibility flow.
   * - Fired when the user starts the questionnaire on the /first-step page.
   */
  static async newQuizStartEvent(userFlow: UserFlow) {
    await this.pushEventToDataLayer("newQuizStart", { userFlow })
  }

  /**
   * Send a assessment viewed event to the data layer for strategic coaching call feasibility flow.
   * - Fired when the user views the assessment after the questionnaire.
   */
  static async newAssessmentViewEvent(userFlow: UserFlow) {
    await this.pushEventToDataLayer("newAssessmentView", { userFlow })
  }

  /**
   * Send a user sign up event to the data layer.
   * - Fired when the user is created AND their quiz is completed.
   */
  static async signUpEvent(userFlow: UserFlow) {
    await this.pushEventToDataLayer("signUp", { userFlow })
  }

  /**
   * Sends an event to the data layer when the user views a list of items.
   */
  static async viewItemListEvent(userFlow: UserFlow, items: CheckoutSessionItem[]) {
    await this.pushEventToDataLayer("viewItemList", { userFlow, items })
  }

  /**
   * Sends an event to the data layer when the user views the select coach page.
   */
  static async viewSelectCoachEvent() {
    await this.pushEventToDataLayer("viewSelectCoach", { userFlow: UserFlow.COACHING })
  }

  // PRIVATE STATIC METHODS

  /**
   * Pushes a custom event to the data layer.
   * We await the promise to ensure the event is sent before continuing.
   * If the event is not sent within 2 seconds, we resolve the promise.
   */
  private static async pushEventToDataLayer(event: GtmEventName, data: DataLayer = {}): Promise<void> {
    // If we are not in production, we do not want to send events to the data layer.
    if (!this.fireEventsInDev && import.meta.env.VITE_ENV !== "prod") {
      console.log(`Simulating GTM event: ${event}`, data)
      return
    }

    return new Promise<void>((resolve) => {
      window.dataLayer = window.dataLayer || []

      const eventCallback = (containerId: string) => {
        if (containerId?.startsWith("GTM-")) {
          resolve()
          return
        }
      }

      // Track user data in GTM for enhanced conversions
      const userStore = useUserStore()
      const user = this.prepareUserData(
        userStore.getUserId,
        userStore.getEmail,
        userStore.getPhoneNumber,
        userStore.getZipCode
      )

      window.dataLayer.push({ event, ...data, eventCallback, user })

      // Resolve after 2 seconds if no callback is called with a GTM container ID
      setTimeout(() => {
        resolve()
      }, 3000)
    })
  }

  private static prepareUserData(
    userId: number | null,
    email: string | null,
    phoneNumber: string | null,
    zipCode: string | null
  ) {
    const user: DataLayer["user"] = {}
    if (userId) {
      user.id = +userId
    }
    if (email && typeof email == "string" && email.trim().length > 0) {
      user.email = email.trim().toLowerCase()
    }
    if (phoneNumber && typeof phoneNumber == "string" && phoneNumber.trim().length > 0) {
      // ensure that the phone number is only numbers
      user.phoneNumber = phoneNumber.trim().replace(/\D/g, "")
    }
    if (zipCode && typeof zipCode == "string" && zipCode.trim().length > 0) {
      user.zipCode = zipCode.trim()
    }

    return user
  }
}
